import React, { useContext, useEffect, useLayoutEffect, useReducer, useRef, useState } from 'react';
import { useRouteMatch, useHistory, Prompt } from 'react-router-dom';

import { ROPatientCarePlanRoute, PreviewDocuments, DocumentType } from '../Interface';
import ROPatientCarePlanPageFooter from '../Footer';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import {
  SUBMIT_PRESCRIPTION,
  TRIGGER_CPOT,
  GET_SUBMISSION_PREVIEW_DOCUMENTS,
  REVERT_CPOT,
  FETCH_PLANS,
  UPDATE_PLAN_MANAGEMENT_USER_SELECTION,
  UPDATE_PLAN_MANAGEMENT_NO_UNAPPROVAL_REQUIRED,
  UPDATE_PLAN_MANAGEMENT_USER_ACKNOWLEDGED_ERROR,
  UPDATE_PLAN_MANAGEMENT_USER_CANCELLATION,
  GENERATE_EDITABLE_AUTO_DOCS,
} from '../Queries';
import { CareplanBanners, changesFromLastEdited, hasSubmitted } from '../Banner/Banner';
import { GET_CAREPLAN, USER_DATA } from '../Queries';
import './SubmissionPage.scss';
import { CurrentAppConfig } from '../AppConfig';
import { useTenantAppConfig } from 'op-utils/useTenantAppConfig';
import { StandardDialog } from 'shared-components/components';
import CareplanContainer from '../Container';
import { UserContext } from 'op-contexts';
import { PermissionType } from 'op-enums';
import ModalCpotCancel from '../ModalCpotCancel/ModalCpotCancel';
import { ModalSimulationSubmission, ROSidePanel } from 'op-components';
import { Region } from 'shared-components/enums';
import { usePreventUnload } from 'shared-components/utils/CustomHooks';
import { CAREPLAN_PAGES } from '../Constants';
import { useWindowSize } from 'shared-components/utils/CustomHooks';
import { LARGE_DISPLAY_SIZE } from 'shared-components/enums';
import { GET_PEER_REVIEW, GET_DIAGNOSIS_FROM_CAREPLAN } from '../SidePanel/SidePanelQueries';
import { Logger } from 'shared-components/utils';
import { CAREPLAN_STATUS, logMissingDiagnosisCode } from '../Common';
import {
  IconButton,
  Fade,
  Snackbar,
  Typography,
  Button,
  Tooltip,
  Badge,
  useTheme,
  SvgIconPropsColorOverrides,
} from '@mui/material';
import { Stack, Card, CardContent, Divider, ListItemButton } from '@mui/material';
import DocumentDisplayPanel from './DocumentDisplayPanel';
import RequestTemplateModal from './RequestTemplateModal';
import dayjs from 'dayjs';
import { TENANT_CONFIG_QUERY } from '../../../../components/RO/Base/TenantConfigQuery';
import { useCareplanDataContext } from '../CareplanContext/CareplanDataContext';
import PlanSelection from './PlanSelection';
import PlanError from './PlanError';
import { Plan, PlansResponse, FetchPlansResponse } from '../Interface';
import { gql } from '@apollo/client';
import { CloseOutlined, Description, Edit, Warning, WarningAmber } from '@mui/icons-material';
import AutoDocEditor from './AutoDocEditor/AutoDocEditor';
import SimRequestAttachmentOnboarding from './SimRequestAttachmentOnboarding';
import UnViewedAutoDocDialog from './UnViewedAutoDocDialog';
import DiscardUnApprovedDocsDialog from './DiscardUnApprovedDocsDialog';
import { hasUnapprovedSimReqAttachment, isSubmissionPreviewReady } from './submissionGating';
import NotificationSnackbar from 'shared-components/components/NotificationSnackbar/NotificationSnackbar';

const REACT_APP_REGION = import.meta.env.REACT_APP_REGION;

const region = REACT_APP_REGION;

const logger = new Logger('SubmissionPage.tsx');
const LABEL_REVERT_CPOT = 'Revert to Previous Prescription';
const LABEL_CANCEL_CPOT = 'Cancel';
const LABEL_CPOT = 'Change prescription on treatment';

const isCPOTCareplanRevertAllowed = (careplan: any): boolean => {
  // if careplan is not CPOT, return true
  if (careplan?.changeType !== 'CPOT') return true;
  const submittedAt = dayjs(careplan?.submittedAt);
  const hoursSinceSubmission = dayjs().diff(submittedAt, 'hours');
  return careplan?.changeType === 'CPOT' && hoursSinceSubmission < 168; // 7 days
};

const typographySectionStyles = {
  margin: '8px 0px',
  padding: '0px 16px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical' as const,
  lineClamp: 2,
  lineHeight: 2,
  maxHeight: '4.2em',
};

const SectionNameWithTooltip = ({ name }: { name: string }): JSX.Element => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const checkOverflow = (): void => {
    const el = wrapperRef.current;
    if (!el) return;
    setIsOverflowing(el.scrollHeight > el.clientHeight);
  };

  useLayoutEffect(() => {
    checkOverflow();
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [name]);

  const content = (
    <div ref={wrapperRef} style={typographySectionStyles as React.CSSProperties}>
      <Typography variant="subtitle1" color="text.primary" sx={{ padding: 0 }}>
        {name}
      </Typography>
    </div>
  );

  return isOverflowing ? (
    <Tooltip title={name} placement="left" arrow>
      {content}
    </Tooltip>
  ) : (
    content
  );
};

const ROPatientTreatmentSubmissionPage = (): JSX.Element => {
  /**
   * @description
   * This page is responsible for displaying the submission page for the careplan.
   * It displays the careplan in a PDF format and allows the user to submit the careplan to MOSAIQ.
   * Scenarios:
   * 1. Plan Aim submission
   *    - show tooltip if there are autodocs to be submitted
   * 2. Prescription submission
   * 3. Prescription resubmission with simulation resubmission
   * 4. Prescription Directive submission
   * 5. CPOT submission
   * 6. CPOT cancellation
   * 7. UK specific prescription resubmission
   */
  const theme = useTheme();
  const windowSize = useWindowSize();
  const isSmallDevice = windowSize.width < LARGE_DISPLAY_SIZE;
  const history = useHistory();
  const [state, setState] = useReducer((state: any, changeState: any): any => ({ ...state, ...changeState }), {
    submitting: false,
    submissionType: CurrentAppConfig.SubmissionPage.PlanAimSubmissionType,
    loading: false,
    error: false,
  });
  const userDetails = useContext(UserContext);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showStartModal, setShowStartModal] = useState(false);
  const [showSimSubmissionModal, setShowSimSubmissionModal] = useState<boolean | undefined>(undefined);
  const [shouldResubmitSimulation, setShouldResubmitSimulation] = useState<boolean | undefined>(undefined);
  const tenantAppConfig = useTenantAppConfig();
  const showSpecialPhysicsConsultPage = tenantAppConfig.SpecialPhysicsConsultVisible;
  const [selectedDocumentObject, setSelectedDocumentObject] = useState<DocumentType | undefined>();
  const [simReqAttachmentAnchorEl, setSimReqAttachmentAnchorEl] = useState<HTMLElement | null>(null);

  const [showRequestTemplateModal, setShowRequestTemplateModal] = useState(false);
  const showNextFeatureSnackbar = userDetails.hasFeature('Next Step');
  const [openNextFeatureSnackbar, setOpenNextFeatureSnackbar] = useState(false);

  // Plan management state
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState<{ type: string; variables: any } | null>(null);
  const [fetchPlansResponse, setFetchPlansResponse] = useState<FetchPlansResponse | null>(null);
  const [isUpdatingPlanManagement, setIsUpdatingPlanManagement] = useState(false);
  const [isAcknowledgingIguanaError, setIsAcknowledgingIguanaError] = useState(false);
  const [showUnViewedAutoDocDialog, setShowUnViewedAutoDocDialog] = useState(false);
  const [showDiscardOnBackDialog, setShowDiscardOnBackDialog] = useState(false);
  const [pendingSubmissionType, setPendingSubmissionType] = useState<string | null>(null);
  const [editableDocumentStatus, setEditableDocumentStatus] = useState<
    { id: string; hasUncommittedChanges: boolean; viewed: boolean }[]
  >([]);

  const match = useRouteMatch<ROPatientCarePlanRoute>();
  const { id: patientId, careplanId } = match.params;
  const { isMRLApplicable } = useCareplanDataContext();
  const [showAutoDocToast, setShowAutoDocToast] = useState(false);

  const [triggerCpot] = useMutation(TRIGGER_CPOT, {
    awaitRefetchQueries: true,
    variables: {
      id: careplanId,
      cpotTriggered: true,
    },
    refetchQueries: [{ query: GET_CAREPLAN, variables: { id: careplanId } }],
  });
  const [cancelCpot] = useMutation(TRIGGER_CPOT, {
    awaitRefetchQueries: true,
    variables: {
      id: careplanId,
      careplanStatus: 'Prescription',
      cpotTriggered: false,
    },
    refetchQueries: [{ query: GET_CAREPLAN, variables: { id: careplanId } }],
  });
  const [revertCpot] = useMutation(REVERT_CPOT, {
    awaitRefetchQueries: true,
    variables: {
      id: careplanId,
    },
    refetchQueries: [
      { query: GET_CAREPLAN, variables: { id: careplanId } },
      { query: GET_SUBMISSION_PREVIEW_DOCUMENTS, variables: { careplanId } },
    ],
  });

  const [submitPrescription] = useMutation(SUBMIT_PRESCRIPTION, {
    refetchQueries: [
      { query: GET_CAREPLAN, variables: { id: careplanId } },
      { query: GET_PEER_REVIEW, variables: { careplanId } },
      { query: GET_SUBMISSION_PREVIEW_DOCUMENTS, variables: { careplanId } },
    ],
    awaitRefetchQueries: true,
  });

  // Plan management mutations and queries
  const [fetchPlans, { loading: fetchPlansLoading, error: fetchPlansError }] = useLazyQuery(FETCH_PLANS, {
    variables: { id: careplanId },
    fetchPolicy: 'network-only',
  });

  const [updatePlanManagementUserCancellation] = useMutation(UPDATE_PLAN_MANAGEMENT_USER_CANCELLATION);
  const [updatePlanManagementUserSelection] = useMutation(UPDATE_PLAN_MANAGEMENT_USER_SELECTION);
  const [updatePlanManagementNoUnapprovalRequired] = useMutation(UPDATE_PLAN_MANAGEMENT_NO_UNAPPROVAL_REQUIRED);
  const [updatePlanManagementUserAcknowledgedError] = useMutation(UPDATE_PLAN_MANAGEMENT_USER_ACKNOWLEDGED_ERROR);

  const { data: tenantConfigData } = useQuery(TENANT_CONFIG_QUERY);

  const handleSubmission = (submissionType: string) => {
    const enableAriaPlanManagement = false;
    // If Aria plan management is enabled and this is a prescription or plan aim submission, check for plans first
    const prescriptionStatuses = ['Plan Aim', 'Prescription', 'Prescription Directive'];

    if (enableAriaPlanManagement && prescriptionStatuses.includes(submissionType)) {
      // Careplan loading state
      setState({ submitting: true, loading: true });

      // Fetch plans first to check if there are any with "Treatment approved" status
      fetchPlans()
        .then((response: any) => {
          // Check if there are GraphQL errors
          if (response?.errors && response.errors.length > 0) {
            // If there's an error fetching plans, proceed with normal submission
            logger.error(`Error fetching plans for careplan id ${careplanId}: ${response.errors[0].message}`);
            executeSubmission(submissionType);
            return;
          }

          const fetchPlansData = response?.data?.fetchPlans;
          try {
            const raw = fetchPlansData?.iguanaResponse;
            const parsedData = typeof raw === 'string' ? JSON.parse(raw || '{}') : raw ?? {};
            const plansData: PlansResponse | Record<string, any> = parsedData;

            if (parsedData.plan_management_disabled) {
              // Plan management is disabled for this patient, proceed with normal submission
              executeSubmission(submissionType);
              return;
            }

            if (parsedData.error) {
              // Iguana Fetch returned an error. Stop processing and show the plan error modal
              setState({ submitting: false, loading: false });
              showPlansModal(submissionType, plansData, fetchPlansData.planManagementId, true, parsedData.message);
              return;
            }

            // Check if there are any plans with "Treatment approved" status
            const plans = plansData?.plans || [];
            const hasTreatmentApprovedPlans = plans.some((plan: any) => plan.plan_status === 'Treatment approved');

            if (hasTreatmentApprovedPlans) {
              // Show the plan selection modal only if there are plans with "Treatment approved" status
              // Clear Careplan loading state
              setState({ submitting: false, loading: false });

              if (fetchPlansData?.planManagementId) {
                showPlansModal(submissionType, plansData, fetchPlansData.planManagementId);
              } else {
                // If no planManagementId, proceed with normal submission
                executeSubmission(submissionType);
              }
            } else {
              // If no plans with "Treatment approved" status, proceed with normal submission
              executeSubmission(submissionType);
            }
          } catch (error) {
            logger.error(`Error parsing plans from iguana: ${fetchPlansData?.iguanaResponse || '{}'}. Error: ${error}`);
            // If there's an error parsing plans, proceed with normal submission
            executeSubmission(submissionType);
          }
        })
        .catch((error: any) => {
          logger.error(`Error fetching plans: ${error?.message || 'Failed to fetch plans'}`);
          // If there's an error fetching plans, proceed with normal submission
          executeSubmission(submissionType);
        });
    } else {
      // Otherwise, proceed with normal submission
      executeSubmission(submissionType);
    }
  };

  const executeSubmission = (submissionType: string, confirmSubmission: boolean = false) => {
    setState({ submitting: true, loading: true });

    const variables = {
      id: careplanId,
      status: submissionType,
      resubmitSimulation: shouldResubmitSimulation,
      ...(confirmSubmission && { confirmSubmission: true }),
    };

    submitPrescription({ variables })
      .then(() => {
        setState({ submitting: false, loading: false });
        setSelectedDocumentObject(undefined);
        if (
          showNextFeatureSnackbar &&
          careplanStatus &&
          [CAREPLAN_STATUS.draft, CAREPLAN_STATUS.dxSubmitted, CAREPLAN_STATUS.simulation].includes(careplanStatus)
        ) {
          setOpenNextFeatureSnackbar(true);
        }
        // Trigger survey event
        if ((window as any)._sva) {
          (window as any)._sva.invokeEvent('showSurvey');
        }
      })
      .catch((error: any) => {
        setState({ submitting: false, loading: false });
      });
  };

  const resetModalState = () => {
    setShowConfirmationModal(false);
    setShowErrorModal(false);
    setPendingSubmission(null);
    setFetchPlansResponse(null);
    setIsUpdatingPlanManagement(false);
    setIsAcknowledgingIguanaError(false);
  };

  const showPlansModal = (
    submissionType: string,
    plansData: PlansResponse | Record<string, any>,
    planManagementId?: string,
    isFetchError: boolean = false,
    errorMessage: string = '',
  ) => {
    setPendingSubmission({
      type: submissionType,
      variables: { id: careplanId, status: submissionType, resubmitSimulation: shouldResubmitSimulation },
    });

    if (isFetchError) {
      setShowErrorModal(true);
    } else {
      setShowConfirmationModal(true);
    }

    // Create the fetchPlansResponse data structure
    const fetchPlansResponseData = {
      iguanaResponse: plansData,
      success: true,
      errorMessage: errorMessage,
      planManagementId: planManagementId,
    };

    setFetchPlansResponse(fetchPlansResponseData);
  };

  const handleConfirmIguanaError = () => {
    // Nothing to do if there's no pending submission
    if (!pendingSubmission) return;

    // Capture the submission type before resetting modal state
    const submissionType = pendingSubmission.type;

    // If planManagementId is absent, just close the modal and proceed
    if (!fetchPlansResponse?.planManagementId) {
      resetModalState();
      return executeSubmission(submissionType, true);
    }

    // Otherwise, guard against duplicate acknowledges and update
    if (!isAcknowledgingIguanaError) {
      setIsAcknowledgingIguanaError(true);
      updatePlanManagementUserAcknowledgedError({
        variables: {
          planManagementId: fetchPlansResponse.planManagementId,
          userAcknowledgedError: true,
        },
      })
        .then((result) => {
          // After successfully updating the plan management record, proceed with submission
          resetModalState();
          executeSubmission(submissionType, true);
        })
        .catch((error) => {
          logger.error('Failed to update plan management user acknowledged error:', error);
          // Even if updating the plan management fails, proceed with submission
          resetModalState();
          executeSubmission(submissionType, true);
        })
        .finally(() => {
          setIsAcknowledgingIguanaError(false);
        });
    }
  };

  const handleClosePlansModal = () => {
    // If there's a plan management ID from the fetch plans response, update the user cancellation
    if (fetchPlansResponse?.planManagementId) {
      updatePlanManagementUserCancellation({
        variables: { planManagementId: fetchPlansResponse.planManagementId },
      }).catch((error) => {
        logger.error('Failed to update plan management user cancellation:', error);
      });
    }

    setShowConfirmationModal(false);
    setShowErrorModal(false);
    setPendingSubmission(null);
  };

  const handleConfirmSubmission = (selectedPlans: Plan[], noUnapprovalRequired: boolean) => {
    if (pendingSubmission && fetchPlansResponse?.planManagementId && !isUpdatingPlanManagement) {
      // Capture the submission type before resetting modal state
      const submissionType = pendingSubmission.type;

      // Set loading state
      setIsUpdatingPlanManagement(true);
      if (noUnapprovalRequired) {
        updatePlanManagementNoUnapprovalRequired({
          variables: {
            planManagementId: fetchPlansResponse.planManagementId,
            noUnapprovalRequired: true,
          },
        })
          .catch((error) => {
            logger.error('Failed to update plan management no unapproval required:', error);
          })
          .finally(() => {
            resetModalState();
            executeSubmission(submissionType, true);
            setIsUpdatingPlanManagement(false);
          });
      } else {
        // plans are selected, update the user_selected_option field
        const userSelectedOption = {
          selectedPlans,
          submissionType: submissionType,
        };
        updatePlanManagementUserSelection({
          variables: {
            planManagementId: fetchPlansResponse.planManagementId,
            userSelectedOption: JSON.stringify(userSelectedOption),
          },
        })
          .catch((error) => {
            logger.error('Failed to update plan management user selection:', error);
          })
          .finally(() => {
            setIsUpdatingPlanManagement(false);
            resetModalState();
            executeSubmission(submissionType, true);
          });
      }
    }
  };

  const hasUnapprovedAutodocChanges = editableDocumentStatus.some((d) => d.hasUncommittedChanges);

  const navigateBack = (): void => {
    if (showSpecialPhysicsConsultPage) {
      history.push(
        `/radiation/patient/${patientId}/careplan/${careplanId}/${CAREPLAN_PAGES.SPECIAL_PHYSICS_CONSULT}/last`,
      );
    } else if (isMRLApplicable) {
      history.push(`/radiation/patient/${patientId}/careplan/${careplanId}/${CAREPLAN_PAGES.MRL_SAFETY_SCREENING}`);
    } else {
      history.push(`/radiation/patient/${patientId}/careplan/${careplanId}/${CAREPLAN_PAGES.VOLUMING}/last`);
    }
  };

  const validateAutoDocs = (submissionType: string): boolean => {
    // Defence in depth: if the preview documents haven't loaded yet we can't tell
    // whether anything needs reviewing/approving, so never let submission through
    // (the submit buttons are also disabled until the preview is ready).
    if (!isSubmissionPreviewReady(previewDataLoading, submissionPreviewData)) {
      return false;
    }
    const uncommittedAutoDoc = editableDocumentStatus.find((d) => d.hasUncommittedChanges);
    const hasUnviewedAutoDocs = editableDocumentStatus.some((d) => !d.viewed);
    const unapprovedAttachment = hasUnapprovedSimReqAttachment(submissionPreviewData?.previewDocuments);
    // If there are any uncommitted autodocs then show a toast notification and the switch to the
    // the first uncommitted autodoc.

    if (uncommittedAutoDoc) {
      setShowAutoDocToast(true);
      const doc = submissionPreviewData?.previewDocuments?.find(
        (d: PreviewDocuments) => d.editableDocumentId === uncommittedAutoDoc.id,
      );
      setSelectedDocumentObject(doc);
    } else if (hasUnviewedAutoDocs || unapprovedAttachment) {
      // Warn (don't hard-block) when an editable doc is unviewed or a sim request
      // attachment hasn't been approved yet. Applies to both Plan Aim and Prescription.
      setPendingSubmissionType(submissionType);
      setShowUnViewedAutoDocDialog(true);
    } else {
      return true;
    }
    return false;
  };

  useQuery(GET_DIAGNOSIS_FROM_CAREPLAN, {
    fetchPolicy: 'network-only',
    variables: { careplanId: careplanId },
    onCompleted: (data: any): void => {
      logMissingDiagnosisCode(logger, data.careplan, 'Submission');
    },
  });

  const { data: careplanData, loading: careplanDataLoading } = useQuery(GET_CAREPLAN, {
    fetchPolicy: 'network-only',
    variables: { id: careplanId },
  });

  const updateEditableDocumentStatus = (
    editableDocumentId: string,
    hasUncommittedChanges: boolean,
    viewed: boolean,
  ) => {
    setEditableDocumentStatus((prev) => [
      ...prev.map((doc) => (doc.id === editableDocumentId ? { ...doc, hasUncommittedChanges, viewed } : doc)),
    ]);
  };

  const {
    data: submissionPreviewData,
    loading: previewDataLoading,
    refetch: previewDocsRefetch,
  } = useQuery(GET_SUBMISSION_PREVIEW_DOCUMENTS, {
    variables: { careplanId },
    skip: !careplanData,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const { data: userData } = useQuery(USER_DATA);
  const [generateEditableAutoDocs, { loading: generateEditableAutoDocsLoading }] =
    useMutation(GENERATE_EDITABLE_AUTO_DOCS);

  // While generating editable auto docs disable submission button
  useEffect(() => {
    if (generateEditableAutoDocsLoading) {
      setState({ submitting: false, loading: true });
    } else {
      setState({ submitting: false, loading: false });
    }
  }, [generateEditableAutoDocsLoading]);

  useEffect(() => {
    if (submissionPreviewData) {
      generateEditableAutoDocs({ variables: { careplanId } });
    }
  }, [submissionPreviewData]);

  useEffect(() => {
    if (submissionPreviewData) {
      if (submissionPreviewData.previewDocuments?.length === 0) {
        setState({ error: true, loading: false });
        return;
      }
      setSelectedDocumentObject(submissionPreviewData.previewDocuments?.[0]);
      // set the editable document status for all editable documents
      setEditableDocumentStatus(
        submissionPreviewData.previewDocuments
          ?.filter((doc: PreviewDocuments) => doc.isEditable)
          .map((doc: PreviewDocuments) => ({
            id: doc.editableDocumentId,
            hasUncommittedChanges: doc.hasUncommittedChanges,
            viewed: doc.viewed,
          })),
      );
    }
  }, [submissionPreviewData, previewDataLoading]);

  const careplanStatus = careplanData?.careplan?.careplanStatus;
  const unloadPrevented =
    [CAREPLAN_STATUS.draft, CAREPLAN_STATUS.cpot, CAREPLAN_STATUS.dxSubmitted].includes(careplanStatus) ||
    Boolean(
      hasSubmitted(careplanData?.careplan, userDetails.state.timezone) && changesFromLastEdited(careplanData?.careplan),
    );

  // check if simulation changes have been made and modal needs enabling. only once, after that the modal
  // will not appear until page is refreshed or navigated to another page
  useEffect(() => {
    if (
      careplanData?.careplan?.simulationModification &&
      !state.submitting &&
      showSimSubmissionModal === undefined &&
      careplanStatus !== CAREPLAN_STATUS.dxSubmitted
    ) {
      setShowSimSubmissionModal(true);
    }
  }, [careplanData?.careplan?.simulationModification, state, showSimSubmissionModal, careplanStatus]);

  // prevent user from leaving page if careplan has not been submitted
  usePreventUnload(unloadPrevented);
  if (!userData) return <></>;
  const { user } = userData;
  const canManageCPOT = userDetails.hasPermission(PermissionType.CAREPLAN_CPOT_MANAGE);
  const canSubmitPrescription = userDetails.hasPermission(PermissionType.PRESCRIPTION_SUBMIT);
  const canRequestTemplateChanges = userDetails.hasPermission(PermissionType.CAREPLAN_TEMPLATE_REQUEST);

  const cpotTriggered = careplanStatus === 'CPOT';
  const disableAllNav =
    careplanData?.careplan?.changeType === 'CPOT' && careplanData?.careplan?.careplanStatus === 'PRESCRIPTION';

  const ukCiedDisabled =
    region === Region.UK &&
    careplanData &&
    careplanData.careplan.cied === 'yes' &&
    (careplanData.careplan.ciedPatientDependancy === 'yes' || careplanData.careplan.ciedLinacRequired === 'yes');
  const planAimSubmission: boolean =
    careplanData &&
    careplanData.careplan &&
    ['PRESCRIPTION', 'PLAN_AIM', 'PRESCRIPTION_DIRECTIVE'].includes(careplanData.careplan.careplanStatus);
  const requestChangesDisabled =
    (Boolean(careplanData?.careplan?.templateChangeRequested) &&
      tenantConfigData?.tenantConfig?.requestTemplateChangeActive) ??
    false;
  // A sim request attachment must be approved before submission. Once the preview
  // has loaded the buttons stay enabled (green) and we warn via the confirmation
  // modal on submit (see validateAutoDocs). True only when such a document exists
  // and is not yet approved (see hasUnapprovedSimReqAttachment).
  const hasUnapprovedAttachment = hasUnapprovedSimReqAttachment(submissionPreviewData?.previewDocuments);

  const footerNextDisabled =
    careplanStatus === 'PRESCRIPTION' ||
    ukCiedDisabled ||
    disableAllNav ||
    cpotTriggered ||
    (planAimSubmission && !changesFromLastEdited(careplanData?.careplan)) ||
    user.isShowcaseUser ||
    // Block submit until the preview documents have loaded — otherwise an early
    // click would bypass the unapproved-attachment / unviewed-autodoc warning
    // (the data needed to gate isn't available yet).
    !isSubmissionPreviewReady(previewDataLoading, submissionPreviewData) ||
    state.loading;

  const footerAdditionRightDisabled =
    (careplanStatus === 'PRESCRIPTION' && !changesFromLastEdited(careplanData?.careplan)) ||
    !canSubmitPrescription ||
    state.loading ||
    !isSubmissionPreviewReady(previewDataLoading, submissionPreviewData) ||
    disableAllNav;

  const nextText = CurrentAppConfig.SubmissionPage.PlanAimSubmissionType; // e.g. Plan Aim
  const nextTextId = nextText.toLowerCase().replace(' ', '-'); // e.g. plan-aim

  // has autodoc documents
  const hasAutoDocDocuments = submissionPreviewData?.previewDocuments?.some(
    (doc: PreviewDocuments) => !['Prescription', 'Simulation Request', 'Peer Review'].includes(doc.documentType),
  );

  const isCPOTRevertAllowed = isCPOTCareplanRevertAllowed(careplanData?.careplan);

  const revertCpotAction = () => {
    setState({ submitting: true, loading: true });
    revertCpot().then(() => {
      setState({ submitting: false, loading: false });
    });
  };

  const shouldShowRequestTemplateButton = (): boolean => {
    return tenantConfigData?.tenantConfig?.requestTemplateChangeActive ?? false;
  };

  const getCPOTButton = () => {
    // CPOT button for PRESCRIPTION status
    if (
      careplanStatus === 'PRESCRIPTION' &&
      !careplanData?.careplan?.diagnosisModification &&
      !careplanData?.careplan?.prescriptionModification &&
      !careplanData?.careplan?.simulationModification &&
      (tenantConfigData?.tenantConfig?.cpotEnabled ?? false) &&
      canManageCPOT
    ) {
      return {
        text: careplanData?.careplan?.changeType === 'CPOT' ? LABEL_REVERT_CPOT : LABEL_CPOT,
        onClick: () => {
          if (careplanData?.careplan?.changeType === 'CPOT') {
            setShowCancelModal(true);
          } else {
            setShowStartModal(true);
          }
        },
        disabled: !isCPOTRevertAllowed,
        tooltip: !isCPOTRevertAllowed ? 'Reversion to previous prescription not possible after 7 days' : '',
        testId: 'cpot-button',
        variant: 'outlined' as const,
      };
    }

    // CPOT cancel button for CPOT status
    if (
      careplanStatus === 'CPOT' &&
      !(tenantConfigData?.tenantConfig?.requestTemplateChangeActive ?? false) &&
      (tenantConfigData?.tenantConfig?.cpotEnabled ?? false) &&
      canManageCPOT
    ) {
      return {
        text: LABEL_CANCEL_CPOT,
        onClick: () => setShowCancelModal(true),
        testId: 'cpot-cancel-button',
        variant: 'outlined' as const,
      };
    }

    return undefined;
  };

  const footerOptions = {
    nextText: nextText,
    nextTextId: nextTextId,
    nextHidden: CurrentAppConfig.SubmissionPage.PlanAimHidden,
    nextTooltip: '',
    resetHidden: true,
    resetDisabled: true,
    resetTooltip: '',
    additionalButtons: [
      // CPOT button (handles both PRESCRIPTION and CPOT statuses)
      ...(getCPOTButton() ? [getCPOTButton()!] : []),
      // Request template button
      ...(shouldShowRequestTemplateButton()
        ? [
            {
              text: 'Request as template',
              onClick: () => setShowRequestTemplateModal(true),
              disabled: !canRequestTemplateChanges || requestChangesDisabled,
              testId: 'request-template-button',
              variant: 'outlined' as const,
            },
          ]
        : []),
    ],
    nextDisabled: footerNextDisabled,
    backDisabled: disableAllNav,
    nextLoading: state.submitting && state.submissionType === CurrentAppConfig.SubmissionPage.PlanAimSubmissionType,
    additionRightText: 'Prescription',
    additionRightTextId: 'prescription',
    additionRightDisabled: footerAdditionRightDisabled,
    onNext: (): void => {
      // only submit after validating the autodocs (shows the confirmation modal
      // for unviewed docs / an unapproved sim request attachment)
      if (validateAutoDocs(CurrentAppConfig.SubmissionPage.PlanAimSubmissionType)) {
        handleSubmission(CurrentAppConfig.SubmissionPage.PlanAimSubmissionType);
      }
    },
    additionRightLoading: state.submitting && state.submissionType === 'Prescription',
    onAdditionRight: () => {
      // only submit after validating the autodocs
      if (validateAutoDocs('Prescription')) {
        handleSubmission('Prescription');
      }
    },
  };
  const additionalURLparam =
    hasSubmitted(careplanData?.careplan, userDetails.state.timezone) && !changesFromLastEdited(careplanData?.careplan)
      ? '?submission=True'
      : '';

  // Organise flat list of documents into sections based on their prescriptionID (which may be null)
  const docSections =
    submissionPreviewData?.previewDocuments?.reduce((prevDict: Record<string, any>, document: PreviewDocuments) => {
      if (!prevDict[document.prescriptionId]) {
        prevDict[document.prescriptionId] = [];
      }
      prevDict[document.prescriptionId].push(document);
      return prevDict;
    }, {}) || {};

  // Convert the doc sections to a list and determine values for display
  const docSectionsList = Object.keys(docSections).map((prescriptionId) => {
    return {
      name: docSections[prescriptionId][0].prescriptionName,
      orderRanking: docSections[prescriptionId][0].orderRanking,
      docs: docSections[prescriptionId].map((doc: any) => {
        return {
          name: doc.documentType,
          subtitle: doc.documentType.startsWith('Prescription') ? doc.prescriptionName : undefined,
          documentType: doc.documentType,
          previewUrl: doc.previewUrl,
          orderRanking: doc.orderRanking,
          isEditable: doc.isEditable,
          editableDocumentId: doc.editableDocumentId,
          html: doc.html,
          hasUncommittedChanges: editableDocumentStatus.find((d) => d.id === doc.editableDocumentId)
            ?.hasUncommittedChanges,
          lastViewedAt: doc.lastViewedAt,
          viewed: editableDocumentStatus.find((d) => d.id === doc.editableDocumentId)?.viewed,
          version: doc.version,
          isSimReqAttachment: doc.isSimReqAttachment,
        };
      }),
    };
  });

  // sorted the documents within the section
  const sectionsWithSortedDocs = docSectionsList.map((doc: any) => {
    return {
      ...doc,
      docs: doc.docs.sort((a: any, b: any) => {
        if (a.orderRanking == null && b.orderRanking == null) return 0;
        if (a.orderRanking == null) return 1; // a goes after b
        if (b.orderRanking == null) return -1; // b goes after a
        return a.orderRanking - b.orderRanking;
      }),
    };
  });

  // Sort the sections themselves
  const sortedDocSections = sectionsWithSortedDocs.sort((a: any, b: any) => {
    // Handle null/undefined orderRanking by placing them at the end
    if (a.orderRanking == null && b.orderRanking == null) return 0;
    if (a.orderRanking == null) return 1; // a goes after b
    if (b.orderRanking == null) return -1; // b goes after a
    return a.orderRanking - b.orderRanking;
  });

  const isPreview =
    (changesFromLastEdited(careplanData?.careplan) ||
      careplanStatus === 'DRAFT' ||
      careplanStatus === 'SIMULATION' ||
      careplanStatus === CAREPLAN_STATUS.dxSubmitted) &&
    docSectionsList?.length > 0;

  // TODO this logic has been replicated during a partial refactor of the view
  // code where we were rendering separate versions of the DocumentDisplayPanel based on isPreview
  // Further cleanup is needed.
  const getUrlToDisplay = () => {
    if (!isPreview) {
      return state.submitting
        ? ''
        : `/ro_portal/combined/${careplanId}/prescription.html${
            additionalURLparam ? `${additionalURLparam}&type=${careplanStatus}` : `?type=${careplanStatus}`
          }`;
    }
    return selectedDocumentObject?.previewUrl || '';
  };

  // Pending AutoDoc changes that are not approved/confirmed
  const pendingAutoDocChanges = sortedDocSections.flatMap((s) => s.docs).filter((d) => d.hasUncommittedChanges);

  return (
    <>
      <Prompt
        when={unloadPrevented}
        message={(location) => {
          return location.pathname.includes(showSpecialPhysicsConsultPage ? '/specialPhysicsConsult' : '/voluming')
            ? true
            : 'This careplan has NOT been submitted. Are you sure you want to leave page without submitting?';
        }}
      />

      {showRequestTemplateModal && (
        <RequestTemplateModal
          open={showRequestTemplateModal}
          careplanId={careplanId}
          onClose={() => setShowRequestTemplateModal(false)}
        />
      )}

      <div className="main-container-parent-wrapper">
        <div className="main-container-wrapper">
          <CareplanBanners
            data={careplanData}
            refetchQueriesList={[{ query: GET_CAREPLAN, variables: { id: careplanId } }]}
          />
          <CareplanContainer className={'pdf-submission-container'}>
            <Stack direction="row" spacing={2} height={1}>
              <Card
                variant="outlined"
                sx={{
                  width: '240px',
                  minWidth: '240px',
                  height: '100%',
                  display: isPreview ? 'flex' : 'none',
                  flexDirection: 'column',
                }}>
                <CardContent
                  sx={{
                    flex: '1 1 auto',
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'auto',
                    padding: '0px',
                  }}>
                  <div style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1, padding: '0px' }}>
                    <Typography data-testid="page-header" variant="h6" color="text.primary" padding="16px">
                      Review & Submit
                    </Typography>
                    <Divider />
                  </div>
                  <Stack overflow="auto" flex="1 1 auto">
                    {sortedDocSections.map((section, index) => {
                      return (
                        <div key={index}>
                          <SectionNameWithTooltip name={section.name} />
                          {section.docs.map((doc: DocumentType, index: number) => {
                            return (
                              <Tooltip
                                key={index}
                                title={doc.isEditable ? 'Editable document' : ''}
                                placement="left"
                                arrow>
                                <ListItemButton
                                  data-testid={`${doc.documentType}`}
                                  ref={doc.isSimReqAttachment ? setSimReqAttachmentAnchorEl : undefined}
                                  onClick={() => setSelectedDocumentObject(doc)}
                                  sx={{
                                    borderLeftColor:
                                      doc.previewUrl === selectedDocumentObject?.previewUrl
                                        ? 'primary.main'
                                        : 'transparent',
                                    borderLeftWidth: '4px',
                                    borderLeftStyle: 'solid',
                                    padding: '8px 0px 8px 16px',
                                    borderRadius: '4px 4px 4px 4px',
                                    backgroundColor:
                                      doc.previewUrl === selectedDocumentObject?.previewUrl
                                        ? 'secondary.light'
                                        : 'default',
                                  }}>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    {doc.isEditable ? <Edit fontSize="small" /> : <Description fontSize="small" />}
                                    <Stack direction="column" width="170px">
                                      <Typography variant="body2" color="text.primary">
                                        {doc.documentType}
                                      </Typography>
                                      {doc.hasUncommittedChanges && (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                          <WarningAmber
                                            fontSize="small"
                                            sx={{ color: theme.palette.warning.dark as SvgIconPropsColorOverrides }}
                                          />
                                          <Typography variant="body2" color="warning.dark">
                                            Unapproved
                                          </Typography>
                                        </Stack>
                                      )}
                                    </Stack>
                                    {doc.isEditable && !doc.viewed && (
                                      <Badge color="primary" variant="dot" data-testid="unviewed-badge" />
                                    )}
                                  </Stack>
                                </ListItemButton>
                              </Tooltip>
                            );
                          })}
                          {section.name && <Divider />}
                        </div>
                      );
                    })}
                  </Stack>
                </CardContent>
              </Card>
              <Card
                sx={{
                  padding: '0px',
                  //minWidth: '794px',
                  flex: '1',
                  minHeight: 0,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: 'none',
                }}>
                {selectedDocumentObject?.isEditable && isPreview ? (
                  <AutoDocEditor
                    key={selectedDocumentObject.editableDocumentId}
                    document={selectedDocumentObject}
                    updateDocumentStatus={updateEditableDocumentStatus}
                    showApproveIndicator={Boolean(selectedDocumentObject.isSimReqAttachment)}
                    onApprove={async (): Promise<void> => {
                      const groupId = selectedDocumentObject.editableDocumentId;
                      const { data } = await previewDocsRefetch();
                      const refreshed = data?.previewDocuments?.find(
                        (d: PreviewDocuments) => d.editableDocumentId === groupId,
                      );
                      if (refreshed && selectedDocumentObject) {
                        setSelectedDocumentObject({
                          ...selectedDocumentObject,
                          previewUrl: refreshed.previewUrl,
                          html: refreshed.html,
                          version: refreshed.version,
                          hasUncommittedChanges: refreshed.hasUncommittedChanges,
                        });
                      }
                    }}
                  />
                ) : (
                  <DocumentDisplayPanel url={getUrlToDisplay()} />
                )}
              </Card>
            </Stack>
            {isPreview && <SimRequestAttachmentOnboarding anchorEl={simReqAttachmentAnchorEl} />}
            {
              <Snackbar
                open={openNextFeatureSnackbar}
                onClose={() => setOpenNextFeatureSnackbar(false)}
                TransitionComponent={Fade}
                TransitionProps={{ timeout: 800 }}
                message="Add next step?"
                key={'Fade'}
                autoHideDuration={10000}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                sx={{ marginBottom: '70px' }}
                action={
                  <>
                    <Button
                      variant="text"
                      onClick={() => {
                        setOpenNextFeatureSnackbar(false);
                        history.push(`/radiation/patient/${patientId}/summary/nextSteps`);
                      }}
                      sx={{ color: 'action.active' }}>
                      ADD
                    </Button>
                    <IconButton size="small" onClick={() => setOpenNextFeatureSnackbar(false)}>
                      <CloseOutlined fontSize="small" sx={{ fill: 'white' }} />
                    </IconButton>
                  </>
                }
              />
            }
          </CareplanContainer>
          <ROPatientCarePlanPageFooter
            onBack={(): void => {
              if (hasUnapprovedAutodocChanges) {
                setShowDiscardOnBackDialog(true);
              } else {
                navigateBack();
              }
            }}
            {...footerOptions}
          />
          <ModalSimulationSubmission
            isOpen={showSimSubmissionModal !== undefined && showSimSubmissionModal}
            onConfirmStart={() => setShowSimSubmissionModal(false)}
            submitFunction={(value: string) => {
              setShouldResubmitSimulation(value === 'yes');
              // reset the pdf preview to load latest simulation request changes
              setSelectedDocumentObject(undefined);
              previewDocsRefetch();
            }}
          />
          <ModalCpotCancel
            isOpen={showCancelModal}
            submitFunction={() => {
              if (careplanStatus === 'CPOT') {
                cancelCpot();
              } else if (careplanData?.careplan?.changeType === 'CPOT') {
                revertCpotAction();
              } else {
                cancelCpot();
              }
            }}
            dismissFunction={() => setShowCancelModal(false)}
            modalType={cpotTriggered ? 'cancel' : 'revert'}
          />
          {/* AutoDoc Dialogs for submitting prescription withunviewed documents */}
          <UnViewedAutoDocDialog
            documentList={sortedDocSections.flatMap((s) => s.docs).filter((d) => d.isEditable)}
            hasUnapprovedAttachment={hasUnapprovedAttachment}
            open={showUnViewedAutoDocDialog}
            onClose={() => {
              setShowUnViewedAutoDocDialog(false);
              setPendingSubmissionType(null);
            }}
            onSubmit={() => {
              setShowUnViewedAutoDocDialog(false);
              const submissionType = pendingSubmissionType;
              setPendingSubmissionType(null);
              if (submissionType) {
                executeSubmission(submissionType, true);
              }
            }}
          />
          {/* AutoDoc Dialog for discarding current changes when navigating back */}
          <DiscardUnApprovedDocsDialog
            open={showDiscardOnBackDialog}
            onClose={() => {
              setShowDiscardOnBackDialog(false);
              // select the first unapproved document
              const uncommittedAutoDoc = editableDocumentStatus.find((d) => d.hasUncommittedChanges);
              if (uncommittedAutoDoc) {
                const doc = submissionPreviewData?.previewDocuments?.find(
                  (d: PreviewDocuments) => d.editableDocumentId === uncommittedAutoDoc.id,
                );
                setSelectedDocumentObject(doc);
              }
            }}
            onSubmit={() => {
              setShowDiscardOnBackDialog(false);
              navigateBack();
            }}
            careplanId={careplanId}
          />
          <StandardDialog
            title="Change prescription on treatment"
            open={showStartModal}
            onClose={() => setShowStartModal(false)}
            secondarySubmitText="Simulation"
            closeText="Cancel"
            onSecondarySubmit={() => {
              triggerCpot();
              history.push(`/radiation/patient/${patientId}/careplan/${careplanId}/simulation`);
            }}
            onSubmit={() => {
              triggerCpot();
              history.push(`/radiation/patient/${patientId}/careplan/${careplanId}/prescription`);
            }}
            submitText={'Prescription'}>
            <Typography paddingY={2}>Would you like to re-request a simulation or change in prescription?</Typography>
          </StandardDialog>
          {/* Plan Selection Modal */}
          <PlanSelection
            open={showConfirmationModal}
            onClose={handleClosePlansModal}
            handleSubmit={handleConfirmSubmission}
            fetchPlansResponse={fetchPlansResponse}
            isLoadingFetchPlans={fetchPlansLoading}
            onExited={() => {
              setFetchPlansResponse(null);
            }}
            isSubmitting={isUpdatingPlanManagement}
          />
          <PlanError
            open={showErrorModal}
            onClose={handleClosePlansModal}
            handleSubmit={handleConfirmIguanaError}
            loading={isAcknowledgingIguanaError}
          />

          <NotificationSnackbar
            open={showAutoDocToast}
            onClose={() => setShowAutoDocToast(false)}
            message="Please approve changes before submitting."
            marginBottom="91px"
            type="notification"
          />
        </div>
      </div>
    </>
  );
};

export default ROPatientTreatmentSubmissionPage;
